import React, { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  Target,
  LineChart,
  Download,
  RefreshCw,
  Eye,
  EyeOff,
  UserCheck,
  UserPlus,
  UserMinus,
  Building2,
  Briefcase,
  Star,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { apiService } from "@/services/api";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

interface UserMetrics {
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  churnedUsers: number;
  retentionRate: number;
  averageSessionDuration: number;
  userSatisfactionScore: number;
}

interface EngagementData {
  date: string;
  dailyActiveUsers: number;
  sessions: number;
  pageViews: number;
  averageSessionDuration: number;
  bounceRate: number;
}

interface RetentionData {
  cohort: string;
  day0: number;
  day1: number;
  day7: number;
  day30: number;
  day90: number;
}

interface UserSegment {
  segment: string;
  count: number;
  percentage: number;
  avgValue: number;
  retentionRate: number;
}

interface GeographicData {
  location: string;
  users: number;
  percentage: number;
  avgEngagement: number;
}

export function AdvancedUserAnalytics() {
  const [timeRange, setTimeRange] = useState("30d");
  const [userType, setUserType] = useState("all");
  const [metrics, setMetrics] = useState<UserMetrics>({
    totalUsers: 0,
    activeUsers: 0,
    newUsers: 0,
    churnedUsers: 0,
    retentionRate: 0,
    averageSessionDuration: 0,
    userSatisfactionScore: 0,
  });
  const [engagementData, setEngagementData] = useState<EngagementData[]>([]);
  const [retentionData, setRetentionData] = useState<RetentionData[]>([]);
  const [userSegments, setUserSegments] = useState<UserSegment[]>([]);
  const [geographicData, setGeographicData] = useState<GeographicData[]>([]);
  const [showDetailedMetrics, setShowDetailedMetrics] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load data from API
  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, userType]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUserAnalytics({
        timeRange,
        userType,
      });

      if (response.type === 1) {
        const data = response.data;
        setMetrics(data.metrics);
        setEngagementData(data.engagementData);
        setUserSegments(data.userSegments);
        setGeographicData(data.geographicData);

        // Generate sample retention data (this would come from backend in real implementation)
        const sampleRetentionData: RetentionData[] = [
          {
            cohort: "Jan 2024",
            day0: 100,
            day1: 85,
            day7: 72,
            day30: 58,
            day90: 45,
          },
          {
            cohort: "Feb 2024",
            day0: 120,
            day1: 98,
            day7: 84,
            day30: 69,
            day90: 52,
          },
          {
            cohort: "Mar 2024",
            day0: 95,
            day1: 82,
            day7: 71,
            day30: 61,
            day90: 48,
          },
        ];
        setRetentionData(sampleRetentionData);
      } else {
        toast.error(response.message || "Failed to load analytics data");
      }
    } catch (error) {
      toast.error("Failed to load analytics data");
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeData = useCallback(() => {
    // Fallback to sample data if API fails
    const baseMetrics = {
      totalUsers: 15420,
      activeUsers: 12470,
      newUsers: 2340,
      churnedUsers: 890,
      retentionRate: 78.5,
      averageSessionDuration: 24.5,
      userSatisfactionScore: 4.2,
    };

    // Adjust metrics based on time range
    const timeMultiplier =
      timeRange === "7d" ? 0.25 : timeRange === "30d" ? 1 : timeRange === "90d" ? 3 : 12;
    const adjustedMetrics = {
      ...baseMetrics,
      totalUsers: Math.floor(baseMetrics.totalUsers * timeMultiplier),
      activeUsers: Math.floor(baseMetrics.activeUsers * timeMultiplier),
      newUsers: Math.floor(baseMetrics.newUsers * timeMultiplier),
      churnedUsers: Math.floor(baseMetrics.churnedUsers * timeMultiplier),
    };

    setMetrics(adjustedMetrics);

    // Sample engagement data
    const generateEngagementData = () => {
      const days =
        timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : timeRange === "90d" ? 90 : 365;
      const data: EngagementData[] = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);

        data.push({
          date: date.toISOString().split("T")[0],
          dailyActiveUsers: Math.floor(Math.random() * 500) + 800,
          sessions: Math.floor(Math.random() * 1000) + 1500,
          pageViews: Math.floor(Math.random() * 3000) + 5000,
          averageSessionDuration: Math.random() * 20 + 15,
          bounceRate: Math.random() * 30 + 20,
        });
      }

      setEngagementData(data);
    };

    // Sample retention data
    const sampleRetentionData: RetentionData[] = [
      {
        cohort: "Jan 2024",
        day0: 100,
        day1: 85,
        day7: 72,
        day30: 58,
        day90: 45,
      },
      {
        cohort: "Feb 2024",
        day0: 120,
        day1: 98,
        day7: 84,
        day30: 69,
        day90: 52,
      },
      {
        cohort: "Mar 2024",
        day0: 95,
        day1: 82,
        day7: 71,
        day30: 61,
        day90: 48,
      },
    ];

    setRetentionData(sampleRetentionData);

    // Sample user segments
    const sampleUserSegments: UserSegment[] = [
      {
        segment: "High Value Employers",
        count: 2340,
        percentage: 15.2,
        avgValue: 299.99,
        retentionRate: 89.5,
      },
      {
        segment: "Standard Employers",
        count: 5670,
        percentage: 36.8,
        avgValue: 199.99,
        retentionRate: 76.3,
      },
      {
        segment: "Active Workers",
        count: 6230,
        percentage: 40.4,
        avgValue: 45.5,
        retentionRate: 82.1,
      },
      {
        segment: "Occasional Workers",
        count: 1180,
        percentage: 7.6,
        avgValue: 18.75,
        retentionRate: 45.2,
      },
    ];

    setUserSegments(sampleUserSegments);

    // Sample geographic data
    const sampleGeographicData: GeographicData[] = [
      {
        location: "New York",
        users: 2340,
        percentage: 15.2,
        avgEngagement: 4.5,
      },
      {
        location: "Los Angeles",
        users: 1980,
        percentage: 12.8,
        avgEngagement: 4.2,
      },
      {
        location: "Chicago",
        users: 1560,
        percentage: 10.1,
        avgEngagement: 4.1,
      },
      {
        location: "Houston",
        users: 1340,
        percentage: 8.7,
        avgEngagement: 3.9,
      },
      {
        location: "Phoenix",
        users: 1120,
        percentage: 7.3,
        avgEngagement: 4.0,
      },
    ];

    setGeographicData(sampleGeographicData);
    generateEngagementData();
  }, [timeRange]);

  // Initialize with sample data
  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportData = () => {
    // Simulate data export
    toast.success("Analytics data exported successfully");
  };

  const handleRefreshData = () => {
    loadAnalyticsData();
    toast.success("Analytics data refreshed");
  };

  const getTrendIcon = (value: number, baseline: number) => {
    if (value > baseline) {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (value < baseline) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    }
    return <Clock className="w-4 h-4 text-gray-600" />;
  };

  const getRetentionColor = (rate: number) => {
    if (rate >= 80) return "text-green-600";
    if (rate >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getSegmentColor = (segment: string) => {
    const colors = {
      "High Value Employers": "bg-blue-100 text-blue-800",
      "Standard Employers": "bg-green-100 text-green-800",
      "Active Workers": "bg-purple-100 text-purple-800",
      "Occasional Workers": "bg-orange-100 text-orange-800",
      Employers: "bg-blue-100 text-blue-800",
      Workers: "bg-green-100 text-green-800",
    };
    return colors[segment as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const getSegmentChartColor = (segment: string) => {
    const colors = {
      "High Value Employers": "#3b82f6",
      "Standard Employers": "#10b981",
      "Active Workers": "#8b5cf6",
      "Occasional Workers": "#f59e0b",
      Employers: "#3b82f6",
      Workers: "#10b981",
    };
    return colors[segment as keyof typeof colors] || "#6b7280";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading analytics data...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Advanced User Analytics</h2>
          <p className="text-muted-foreground">
            Deep insights into user engagement, retention, and behavior patterns
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              setShowDetailedMetrics(!showDetailedMetrics);
            }}
          >
            {showDetailedMetrics ? (
              <EyeOff className="w-4 h-4 mr-2" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            {showDetailedMetrics ? "Hide" : "Show"} Detailed Metrics
          </Button>
          <Button variant="outline" onClick={handleExportData}>
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
          <Button variant="outline" onClick={handleRefreshData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="space-y-2">
          <Label htmlFor="time-range">Time Range</Label>
          <SelectField
            value={timeRange}
            onValueChange={setTimeRange}
            options={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
              { value: "1y", label: "Last year" },
            ]}
            placeholder="Select time range"
            className="w-32"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="user-type">User Type</Label>
          <SelectField
            value={userType}
            onValueChange={setUserType}
            options={[
              { value: "all", label: "All Users" },
              { value: "workers", label: "Workers Only" },
              { value: "employers", label: "Employers Only" },
            ]}
            placeholder="Select user type"
            className="w-40"
          />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalUsers.toLocaleString()}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              {getTrendIcon(metrics.totalUsers, 15000)}
              <span>
                +{(((metrics.totalUsers - 15000) / 15000) * 100).toFixed(1)}% from last period
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <UserCheck className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeUsers.toLocaleString()}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              {getTrendIcon(metrics.activeUsers, 12000)}
              <span>
                {((metrics.activeUsers / metrics.totalUsers) * 100).toFixed(1)}% of total users
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Retention Rate</CardTitle>
            <Target className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.retentionRate}%</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              {getTrendIcon(metrics.retentionRate, 75)}
              <span>Industry average: 75%</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Satisfaction Score</CardTitle>
            <Star className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.userSatisfactionScore}/5.0</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              {getTrendIcon(metrics.userSatisfactionScore, 4.0)}
              <span>
                +{(((metrics.userSatisfactionScore - 4.0) / 4.0) * 100).toFixed(1)}% from target
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics (Conditional) */}
      {showDetailedMetrics && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">New vs Churned Users</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserPlus className="w-4 h-4 text-green-600" />
                  <span className="text-sm">New Users</span>
                </div>
                <span className="font-medium text-green-600">
                  +{metrics.newUsers.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserMinus className="w-4 h-4 text-red-600" />
                  <span className="text-sm">Churned Users</span>
                </div>
                <span className="font-medium text-red-600">
                  -{metrics.churnedUsers.toLocaleString()}
                </span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span>Net Growth</span>
                  <span
                    className={`font-medium ${
                      metrics.newUsers - metrics.churnedUsers > 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {metrics.newUsers - metrics.churnedUsers > 0 ? "+" : ""}
                    {(metrics.newUsers - metrics.churnedUsers).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Session Analytics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Avg Session Duration</span>
                <span className="font-medium">{metrics.averageSessionDuration.toFixed(1)} min</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Daily Active Rate</span>
                <span className="font-medium">
                  {((metrics.activeUsers / metrics.totalUsers) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span>Engagement Score</span>
                  <span className="font-medium">
                    {Math.round(
                      (metrics.averageSessionDuration / 30) *
                        (metrics.activeUsers / metrics.totalUsers) *
                        100
                    )}
                    /100
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">User Type Distribution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">Employers</span>
                </div>
                <span className="font-medium">
                  ~{Math.round(metrics.totalUsers * 0.52).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Briefcase className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Workers</span>
                </div>
                <span className="font-medium">
                  ~{Math.round(metrics.totalUsers * 0.48).toLocaleString()}
                </span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span>Ratio</span>
                  <span className="font-medium">52:48</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for different analytics views */}
      <Tabs defaultValue="engagement" className="space-y-4">
        <TabsList>
          <TabsTrigger value="engagement">Engagement Trends</TabsTrigger>
          <TabsTrigger value="retention">Retention Analysis</TabsTrigger>
          <TabsTrigger value="segments">User Segments</TabsTrigger>
          <TabsTrigger value="geographic">Geographic Insights</TabsTrigger>
        </TabsList>

        {/* Engagement Trends Tab */}
        <TabsContent value="engagement" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Engagement Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {engagementData[engagementData.length - 1]?.dailyActiveUsers.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Today's Active Users</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {engagementData[engagementData.length - 1]?.sessions.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Today's Sessions</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {engagementData[engagementData.length - 1]?.pageViews.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Today's Page Views</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {engagementData[engagementData.length - 1]?.averageSessionDuration.toFixed(1)}
                      m
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Session Duration</div>
                  </div>
                </div>

                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={engagementData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={value =>
                          new Date(value).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })
                        }
                      />
                      <YAxis />
                      <Tooltip
                        labelFormatter={value => new Date(value).toLocaleDateString()}
                        formatter={(value, name) => [
                          typeof value === "number" ? value.toLocaleString() : value,
                          name === "dailyActiveUsers"
                            ? "Daily Active Users"
                            : name === "sessions"
                            ? "Sessions"
                            : name === "pageViews"
                            ? "Page Views"
                            : name === "averageSessionDuration"
                            ? "Avg Session Duration (min)"
                            : name === "bounceRate"
                            ? "Bounce Rate (%)"
                            : name,
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="dailyActiveUsers"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="Daily Active Users"
                      />
                      <Line
                        type="monotone"
                        dataKey="sessions"
                        stroke="#10b981"
                        strokeWidth={2}
                        name="Sessions"
                      />
                      <Line
                        type="monotone"
                        dataKey="pageViews"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        name="Page Views"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retention Analysis Tab */}
        <TabsContent value="retention" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cohort Retention Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Cohort</th>
                      <th className="text-center p-2">Day 0</th>
                      <th className="text-center p-2">Day 1</th>
                      <th className="text-center p-2">Day 7</th>
                      <th className="text-center p-2">Day 30</th>
                      <th className="text-center p-2">Day 90</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retentionData.map(cohort => (
                      <tr key={cohort.cohort} className="border-b">
                        <td className="p-2 font-medium">{cohort.cohort}</td>
                        <td className="p-2 text-center">{cohort.day0}</td>
                        <td className="p-2 text-center">
                          <span className={getRetentionColor((cohort.day1 / cohort.day0) * 100)}>
                            {((cohort.day1 / cohort.day0) * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={getRetentionColor((cohort.day7 / cohort.day0) * 100)}>
                            {((cohort.day7 / cohort.day0) * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={getRetentionColor((cohort.day30 / cohort.day0) * 100)}>
                            {((cohort.day30 / cohort.day0) * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <span className={getRetentionColor((cohort.day90 / cohort.day0) * 100)}>
                            {((cohort.day90 / cohort.day0) * 100).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Segments Tab */}
        <TabsContent value="segments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Segmentation Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={userSegments as any}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ segment, percentage }: any) =>
                          `${segment}: ${percentage.toFixed(1)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="segment"
                      >
                        {userSegments.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getSegmentChartColor(entry.segment)} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [value?.toLocaleString() ?? value, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Segment Details */}
                <div className="space-y-4">
                  {userSegments.map(segment => (
                    <div key={segment.segment} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Badge className={getSegmentColor(segment.segment)}>
                            {segment.segment}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {segment.percentage.toFixed(1)}% of total
                          </span>
                        </div>
                        <span className="text-lg font-bold">{segment.count.toLocaleString()}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Avg Value</div>
                          <div className="font-medium">${segment.avgValue.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Retention Rate</div>
                          <div
                            className={`font-medium ${getRetentionColor(segment.retentionRate)}`}
                          >
                            {segment.retentionRate.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Geographic Insights Tab */}
        <TabsContent value="geographic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Geographic User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {geographicData.length > 0 ? (
                <div className="space-y-6">
                  {/* Bar Chart */}
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={geographicData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="location" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => [
                            typeof value === "number" ? value.toLocaleString() : value,
                            name === "users"
                              ? "Users"
                              : name === "avgEngagement"
                              ? "Avg Engagement"
                              : name,
                          ]}
                        />
                        <Bar dataKey="users" fill="#3b82f6" name="Users" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Location Details */}
                  <div className="space-y-4">
                    {geographicData.map(location => (
                      <div
                        key={location.location}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{location.location}</span>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="text-center">
                            <div className="text-sm text-muted-foreground">Users</div>
                            <div className="font-medium">{location.users.toLocaleString()}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-muted-foreground">Percentage</div>
                            <div className="font-medium">{location.percentage.toFixed(1)}%</div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-muted-foreground">Engagement</div>
                            <div className="font-medium">
                              {location.avgEngagement.toFixed(1)}/5.0
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="w-12 h-12 mx-auto mb-2" />
                  <p>No geographic data available</p>
                  <p className="text-sm">User location data is not available in the database</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
